import React, { createContext, useContext, useState, useEffect } from 'react';
import { transportProviderAPI } from '../services/api';

// Mirrors OfficeAuthContext.js exactly, one org type over.
const TransportProviderAuthContext = createContext(null);

// This portal's session lives purely in an httpOnly cookie (no localStorage),
// but PassengerNotificationContext — which wraps the whole app, above this
// provider in the tree, so it can't read this context directly — needs a
// user id to open its notification socket for whoever's logged into this
// portal too. A plain id marker is the least invasive bridge between the two
// without restructuring either context.
// The marker is written asynchronously (after the me()/login() network round
// trip resolves), unlike the passenger portal's localStorage write which
// happens synchronously at login — so a plain "re-check on route change"
// listener in PassengerNotificationContext could miss a fresh page load that
// never navigates. This event lets it react the moment the marker actually
// changes, not just on the next route change.
const setNotifiableUserId = (userId) => {
  if (userId) localStorage.setItem('transport_provider_user_id', String(userId));
  else localStorage.removeItem('transport_provider_user_id');
  window.dispatchEvent(new Event('saferNotifiableUserChanged'));
};

export const TransportProviderAuthProvider = ({ children }) => {
  const [session, setSession] = useState(null); // { user, provider, providerRole }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    transportProviderAPI.me()
      .then((res) => { setSession(res.data.data); setNotifiableUserId(res.data.data?.user?.id); })
      .catch(() => { setSession(null); setNotifiableUserId(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await transportProviderAPI.login({ email, password });
    const { user, provider, providerRole } = res.data.data;
    setSession({ user, provider, providerRole });
    setNotifiableUserId(user?.id);
    return { user, provider, providerRole };
  };

  const logout = async () => {
    try {
      await transportProviderAPI.logout();
    } catch {
      // ignore network errors — clear local state regardless
    }
    setSession(null);
    setNotifiableUserId(null);
  };

  return (
    <TransportProviderAuthContext.Provider value={{ session, login, logout, loading }}>
      {children}
    </TransportProviderAuthContext.Provider>
  );
};

export const useTransportProviderAuth = () => useContext(TransportProviderAuthContext);
